import { WitzoChatElement } from './chat/chat-widget';
import { templateWidget } from './template';
import { BaseUIElements, Flags, Nullable } from './types';
import { WitzoVoiceElement } from './voice/voice-widget';

const widget = document.createElement('template');
widget.innerHTML = templateWidget;

class WitzoVoiceChat extends HTMLElement {
  root: ShadowRoot;
  /**Url to connect to the webhook */
  private _url: Nullable<string> = null;
  private _agentId: Nullable<string> = null;
  private _defaultText: Nullable<string> = null;
  private _botBackgroundColor: Nullable<string> = null;
  private _sendBackgroundColor: Nullable<string> = null;
  private _floatingBtnColor: Nullable<string> = null;
  private _chatIconColor: Nullable<string> = null;
  private _voiceIconColor: Nullable<string> = null;

  /**Reference for elements */
  chat: Nullable<BaseUIElements> = null;
  flags: Flags;

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'closed' });
    root.appendChild(node);

    this.flags = {
      isInitialized: false,
      isOpen: false,
      isButtonClicked: false,
    };
    this.root = root;
    this.getElements(root);
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const query = <T extends HTMLElement>(selector: string) => root.querySelector(selector) as T;

    try {
      this.chat = {
        chatSelectBtn: query('#selectTextChat'),
        chatWindow: query('#chatWidget'),
        floatingButton: query('#floatingBtn'),
        selectionWindow: query('#selectionPopup'),
        voiceSelectBtn: query('#selectVoiceChat'),
        floatingBtn: query('#floating-btn'),
        chatIcon: root.querySelector('#chat-icon-color') as SVGPathElement,
        voiceIcon: root.querySelector('#voice-icon-color') as SVGPathElement,
      };
      this.flags.isInitialized = true;
    } catch (err) {
      this.flags.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description external function to set the url to connect to web-hook
   */
  set chatInfo(info: {
    url: string;
    defaultText: Nullable<string>;
    botColor: Nullable<string>;
    sendColor: Nullable<string>;
    floatingBtn: Nullable<string>;
    iconColor: Nullable<string>;
    uniqueId:Nullable<string>;
  }) {
    this._url = info.url;
    this._defaultText = info.defaultText ?? null;
    this._botBackgroundColor = info.botColor ?? null;
    this._sendBackgroundColor = info.sendColor ?? null;
    this.defineChatAgent(
      info.url,
      info.defaultText,
      info.botColor,
      info.sendColor,
      info.floatingBtn,
      info.iconColor,
      info.uniqueId
    );
  }

  private defineChatAgent(
    url: string,
    defaultText: Nullable<string>,
    botColor: Nullable<string>,
    sendColor: Nullable<string>,
    floatingBtn: Nullable<string>,
    iconColor: Nullable<string>,
    uniqueId:Nullable<string>
  ) {
    customElements.define('witzo-chat-element', WitzoChatElement);
    const chatElement = this.root.querySelector('witzo-chat-element') as WitzoChatElement;
    chatElement.chatUrl = url;
    chatElement.botBackgroundColor = botColor ?? '#fc0e3f';
    chatElement.sendBackgroundColor = sendColor ?? '#fc0e3f';
    chatElement.uniqueId=uniqueId ?? '';

    chatElement.defaultPrimaryText =
      defaultText ??
      `**Hey there, I’m Iniya! ** 
Got questions about Webomindapps? Ask me anything and I’ll provide you with the info you’re looking for.  

** What I Can Help With..**
- I want to know about the UI/UX services  
- Inform the sales team to reach out to me  
- What are the digital marketing services offered?  
- I want to visit the office  
- Is the company experienced with Shopify?  
- Share some recent projects  
** Note **
There’s much more I can answer — feel free to ask!`;

    chatElement.mainWindow = {
      back: () => {
        this.chat?.selectionWindow.classList.remove('hidden');
        this.chat?.chatWindow.classList.add('hidden');
        this.toggleFloatingButton(false);
      },
      close: () => {
        this.chat?.chatWindow.classList.add('hidden');
        this.toggleFloatingButton(false);
      },
    };

    if (this.chat) {
      this.chat.chatElement = chatElement;
      this.chat.floatingBtn.style.backgroundColor = floatingBtn ?? '#fc0e3f';
      this.chat.chatIcon.style.fill = iconColor ?? '#fc0e3f';
      this.chat.voiceIcon.style.fill = iconColor ?? '#fc0e3f';
    }
  }

  set voiceInfo(info: { agentId: string; iconColor: Nullable<string> }) {
    this._agentId = info.agentId;
    this.defineVoiceAgent(info.agentId, info.iconColor);
  }

  private toggleFloatingButton(isHidden: boolean) {
    this.chat?.floatingButton.classList.remove('hidden');
    if (isHidden) {
      this.chat?.floatingButton.classList.add('hidden');
    }
  }

  private defineVoiceAgent(agentId: string, iconColor: Nullable<string>) {
    customElements.define('witzo-voice-element', WitzoVoiceElement);

    const voiceElement = this.root.querySelector('witzo-voice-element') as WitzoVoiceElement;
    voiceElement.agentId = agentId;
    voiceElement.iconColors = {
      iconColors: iconColor ?? '#fc0e3f',
    };
    voiceElement.mainWindow = {
      back: () => {
        this.chat?.selectionWindow.classList.remove('hidden');
        this.chat?.chatWindow.classList.add('hidden');
        this.toggleFloatingButton(false);
      },
      close: () => {
        this.chat?.chatWindow.classList.add('hidden');
        this.toggleFloatingButton(false);
      },
    };

    if (this.chat) this.chat.voiceElement = voiceElement;
  }

  connectedCallback() {
    this.bindEvents();

    setTimeout(() => {
      if (this.flags.isButtonClicked === false) {
        this.toggleButton();
      }
    }, 30000);
  }

  bindEvents() {
    if (!this.chat || this.flags.isInitialized === false) return;
    const { chatSelectBtn, chatWindow, floatingButton, selectionWindow, voiceSelectBtn } =
      this.chat;

    floatingButton.addEventListener('click', () => {
      this.toggleButton();
    });

    chatSelectBtn.addEventListener('click', () => {
      this.openChat();
      this.toggleFloatingButton(true);
    });

    voiceSelectBtn.addEventListener('click', () => {
      this.openVoice();
      this.toggleFloatingButton(true);
    });
  }

  /**
   * @description toggle button for floating button
   */
  toggleButton() {
    if (!this.chat) return;
    this.flags.isButtonClicked = true;
    this.flags.isOpen = !this.flags.isOpen;
    this.flags.isOpen ? this.maximizeWidget() : this.minimizeWidget();
  }

  openChat() {
    if (!this.chat) return;

    this.chat.selectionWindow.classList.add('hidden');
    this.chat.chatWindow.classList.remove('hidden');
    this.chat.voiceElement?.classList.add('hidden');
    this.chat.chatElement?.classList.remove('hidden');
  }

  openVoice() {
    if (!this.chat) return;

    this.chat.selectionWindow.classList.add('hidden');
    this.chat.chatWindow.classList.remove('hidden');
    this.chat.chatElement?.classList.add('hidden');
    this.chat.voiceElement?.classList.remove('hidden');
  }

  /**Minimises the chat window */
  private minimizeWidget() {
    if (!this.chat) return;

    this.chat.selectionWindow.classList.add('hidden');
  }

  /**Maximize the chat window */
  private maximizeWidget() {
    if (!this.chat) return;
    this.chat.selectionWindow.classList.remove('hidden');
  }
}

export { WitzoVoiceChat };

